import * as React from 'react';
import { Location } from 'history';
import { Link, useLocation, useParams } from 'react-router-dom';

import { SxProps } from '@mui/system';
import { Divider, ListItem, ListItemText, useTheme, Theme, useMediaQuery, Tooltip, ListItemIcon } from '@mui/material';

import { isSeparator, NavItem, weights } from '../../context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { CerebroIcon } from '..';

type NavItemsProps =
	| {
			level: 1;
			navItems: NavItem[];
	  }
	| {
			level: 2 | 3 | undefined;

			parentKey: string;
			topLevelSectionUrl?: string;

			theme: Theme;
			location: Location;

			navItems: NavItem[];
	  };

export const NavItems: React.FC<NavItemsProps> = props => {
	const theme = useTheme();
	const location = useLocation();
	const { guid } = useParams<'guid'>();

	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	if (props.level === undefined) return null;

	return (
		<>
			{props.navItems.map(navItem => {
				if (isSeparator(navItem)) {
					return <Divider key={navItem.key} sx={{ marginY: isMobile ? 1 : 3 }} />;
				}

				const selected = location.pathname.endsWith(navItem.url);

				const sectionUrl = props.level === 1 ? navItem.urlRootForSection : props.topLevelSectionUrl;

				const sectionSelected = sectionUrl !== undefined ? location.pathname.includes(sectionUrl) : false;

				const expandChildren =
					navItem.urlRootForSection !== undefined
						? location.pathname.includes(navItem.urlRootForSection)
						: false;

				const key = props.level === 1 ? navItem.text : `${props.parentKey} | ${navItem.text}`;

				let nextLevel: NavItemsProps['level'] = undefined;

				switch (props.level) {
					case 1:
						nextLevel = 2;
						break;

					case 2:
						nextLevel = 3;
						break;
				}

				const isHeading = navItem.children.length > 0;

				const listItemProps = navItem.absolute
					? { component: 'a', href: navItem.url, target: '_blank' }
					: { component: Link, to: `${guid}/${navItem.url}` };

				return (
					<React.Fragment key={key}>
						<Tooltip arrow title={navItem.tooltip?.(navItem) ?? ''} placement="right">
							<span>
								<ListItem
									key={key}
									dense
									button
									disabled={navItem.disabled}
									sx={GetListItemStyles(props.level, sectionSelected, selected && !isHeading)}
									{...listItemProps}
								>
									<ListItemText
										primary={navItem.text}
										primaryTypographyProps={{ sx: GetTypographyStyles(props.level) }}
									/>
									{navItem.absolute && (
										<CerebroIcon sx={theme => ({ color: theme.palette.primary.main })}>
											<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
										</CerebroIcon>
									)}
								</ListItem>
							</span>
						</Tooltip>

						{expandChildren && navItem.children.length > 0 && (
							<NavItems
								key={`${key} | Children`}
								level={nextLevel}
								theme={theme}
								parentKey={key}
								location={location}
								navItems={navItem.children}
								topLevelSectionUrl={navItem.urlRootForSection}
							/>
						)}
					</React.Fragment>
				);
			})}
		</>
	);
};

function GetTypographyStyles(level: NavItemsProps['level']): SxProps<Theme> {
	if (level === undefined) return {};

	return theme => ({
		color: theme.palette.gray.dark,
		lineHeight: '18px',

		...(level === 1 ? { fontSize: 20, fontWeight: weights.bold } : {}),

		...(level === 2 ? { fontSize: 14, fontWeight: weights.bold } : {}),

		...(level === 3 ? { fontSize: 12, fontWeight: weights.regular } : {}),
	});
}

function GetListItemStyles(
	level: NavItemsProps['level'],
	sectionSelected: boolean,
	selected: boolean = false
): SxProps<Theme> {
	if (level === undefined) return {};

	return theme => ({
		borderLeftWidth: 3,
		borderLeftStyle: 'solid',
		borderLeftColor: sectionSelected ? theme.palette.primary.main : 'transparent',

		borderRightWidth: 4,
		borderRightStyle: 'solid',
		borderRightColor: { xs: sectionSelected ? theme.palette.primary.main : 'transparent', sm: 'transparent' },

		...(level === 1 ? { marginTop: { xs: 0.5, sm: 1.5 } } : {}),

		...(level === 2 ? { paddingLeft: 3 } : {}),

		...(level === 3 ? { paddingLeft: 4 } : {}),

		...(selected
			? {
					overflow: 'hidden',
					'::before': {
						backgroundColor: theme.palette.primary.main,
						content: '""',
						height: 12,
						position: 'absolute',
						transform: 'rotate(45deg)',
						left: -6,
						width: 12,
					},

					'::after': {
						backgroundColor: { xs: theme.palette.primary.main, sm: 'unset' },
						content: { xs: '""', sm: 'unset' },
						height: { xs: 12, sm: 'unset' },
						position: { xs: 'absolute', sm: 'unset' },
						transform: { xs: 'rotate(45deg)', sm: 'unset' },
						right: { xs: -6, sm: 'unset' },
						width: { xs: 12, sm: 'unset' },
					},
			  }
			: {}),
	});
}
